<template>
    <div :class="['acquisit-elhub-response', { 'acquisit-has-errors': model?.status && model.status !== 'success', ['status-' + (model?.status ?? '')]: model?.status }]">
		<template v-if="model?.status === 'success'">
			<acquisit-info-box :label="model.message"
			                   type="success" />
			
			<div class="actions" v-if="shouldShowRetryWhenSuccessful">
				<acquisit-button v-if="retryLabel"
				                 type="text"
				                 icon="acq/refresh"
				                 :label="retryLabel"
				                 color="red"
				                 @click="retry" />
			</div>
		</template>
		
		<template v-else-if="model?.status === 'error'">
			<acquisit-info-box type="error">
				<acquisit-string :source="model.message" />
				
				<div class="error-code" v-if="shouldShowErrorCodes && model.error">
					{{ errorCode ?? '–' }}
				</div>
			</acquisit-info-box>
			
			<div class="actions">
				<acquisit-button v-if="metersPageLabel && errorCode == ElhubConsentErrorCode.ELHUB_PAYLOAD_INVALID"
				                 type="raised"
				                 icon="arrows/left"
				                 :label="metersPageLabel"
				                 @click="goToMetersPage" />
				
				<acquisit-button v-if="retryLabel"
				                 type="raised"
				                 :bordered="metersPageLabel && errorCode == ElhubConsentErrorCode.ELHUB_PAYLOAD_INVALID"
				                 icon="acq/refresh"
				                 :label="retryLabel"
				                 @click="retry" />
			</div>
		</template>
		
		<template v-else-if="loading.storeBoolean.value['elhub']">
			<div class="processing">
				<acquisit-spinner size="mini" full-width />
			</div>
		</template>
	    
	    <acquisit-modal v-if="model && invalidPersonNumberModal"
	                    :title="invalidPersonNumberModal.modal.title"
	                    :close_on_overlay_click="false"
	                    :show_close_button="false"
	                    ref="$personNumberModal">
		    
		    <template #default>
			    <acquisit-info-box type="error"
			                       :label="model.message" />
			    
			    <acquisit-span v-if="invalidPersonNumberModal?.modal.body" :label="invalidPersonNumberModal.modal.body" />
			    
			    <acquisit-input v-model="personNumber"
			                    :label="invalidPersonNumberModal.personNumberInputLabel"
			                    format="DDDDDDDDDDD"
			                    inputmode="numeric"
			                    pattern="[0-9]*" />
		    </template>
		    
		    <template #left>
			    <acquisit-button type="flat"
			                     class="cancel-button"
			                     color="red"
			                     @click="closePersonNumberModal"
			                     :label="invalidPersonNumberModal.modal.button_decline_label ?? 'Cancel'" />
		    </template>
		    
		    <template #right>
			    <acquisit-button type="raised"
			                     @click="onClickModalRetry"
			                     class="retry-button"
			                     :disabled="!personNumberValid"
			                     :no-interact="!personNumberValid"
			                     :label="invalidPersonNumberModal.modal.button_accept_label" />
		    </template>
	    
	    </acquisit-modal>
    </div>
</template>

<script setup lang="ts">
	import { useBaseComponent, useBaseComponentEmits, useBaseComponentProps, useLoadingHelper } from '@broker-as/acquisit-ui-vue/helpers'
	import { EventBus, useComponentLogger, type Modal, type ModalCompound, type UILabel } from '@broker-as/acquisit-ui-vue/library'
	import { componentHasSemantic, sleep } from '@broker-as/acquisit-ui-vue/functions'
	import { computed, nextTick, onMounted, type PropType, ref, watch, watchEffect } from 'vue'
	import { ElhubConsentErrorCode, isElhubConsentErrorResponse, useBackend } from '@/lib/backend-cors'
	import { useAPIStore } from '@/stores/api'
	import type { UILabelOptional } from '@broker-as/acquisit-ui-vue/library'
	import { useRoute, useRouter } from 'vue-router'
	import type { ElhubControllerModelValue } from '@/lib/types.ts'
	import { useLogsStore } from '@/stores/log.ts'
	import { SEMANTIC_ELECTRICITY, SEMANTIC_METERS, useProtocolStore } from '@/stores/protocol.ts'
	import { usePageRouter } from '@/helpers/page-router.ts'
	import { useUIStore } from '@/stores/ui.ts'
	import { usePageStore } from '@/stores/page.ts'
	
	const props = defineProps({
		...useBaseComponentProps(),
		
		genericErrorLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: 'Det oppstod en feil. Du kan fortsette, og kundeservice vil ta kontakt.'
		},
		
		continueAnywayLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},
		
		retryLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},
		
		/**
		 * Label for the navigation button when elhub was successful already
		 */
		successfulContinueLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},
		
		metersPageLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},
		
		/**
		 * When the user aborted the process
		 */
		rejectedLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},
		
		postponedLabel: {
			type: [String, Object] as PropType<UILabelOptional>,
			required: false,
			default: null
		},

		modelValue: {
			type: Object as PropType<ElhubControllerModelValue>,
			required: false,
			default: null
		},
		
		invalidPersonNumberModal: {
			type: Object as PropType<{ modal: ModalCompound, personNumberInputLabel: UILabel }>,
			required: false,
			default: null
		}
	})
	
	const emit = defineEmits([
		...useBaseComponentEmits(),
		'update:modelValue'
	])
	
	const loading = useLoadingHelper()
	const $router = useRouter()
	const $route = useRoute()
	const pageRouter = usePageRouter(props)
	const base = useBaseComponent(props, emit)
	const { color } = base
	const ui = useUIStore()
	const protocolStore = useProtocolStore()
	const log = useComponentLogger('components/ui/product/ElhubController', props)
	const model = ref<ElhubControllerModelValue|null|undefined>()
	const $personNumberModal = ref<typeof Modal|undefined>()
	
	const shouldShowErrorCodes = computed(() => import.meta.env.DEV || import.meta.env.MODE == 'staging' || (<any> window).DEBUG)
	const shouldShowRetryWhenSuccessful = computed(() => import.meta.env.DEV || (<any> window).DEBUG)
	
	const errorCode = computed(() => (<any> model.value?.error)?.error_code)
	
	const personNumber = computed({
		get() {
			return protocolStore.responsible_person_for_electricity?.document_signing_ssn
		},
		
		set(ssn) {
			const person = protocolStore.responsible_person_for_electricity
			
			if (person) {
				person.document_signing_ssn = ssn
			}
		}
	})
	
	const personNumberValid = computed(() => {
		if (!personNumber.value) {
			return false
		}
		
		return String(personNumber.value).length === 11
	})
	
	watchEffect(() => {
		model.value = props.modelValue
	})
	
	const setProcessing = (processing: boolean) => {
		EventBus.$emit('elhub:processing', processing)
		
		if (processing) {
			ui.elhub_loading = true
			
			loading.set('elhub')
			
			model.value = {
				status: 'processing'
			}
		} else {
			loading.done('elhub')
			
			if (model.value?.status === 'processing') {
				model.value = null
			}
			
			ui.elhub_loading = false
		}
		
		emit('update:modelValue', model.value)
	}
	
	const reset = () => {
		model.value = null
		emit('update:modelValue', model.value)
	}
	
	const retry = async () => {
		model.value = null
		emit('update:modelValue', null)
		
		await nextTick()
		EventBus.$emit('navigation:validate')
	}
	
	const onClickModalRetry = async () => {
		await closePersonNumberModal()
		await retry()
	}
	
	const goToMetersPage = () => {
		// Find page
		const protocolStore = useProtocolStore()
		const electricityMetersPage = protocolStore.findPageWithComponent((c) => componentHasSemantic(c, [ SEMANTIC_ELECTRICITY, SEMANTIC_METERS ], 'AND'))
		
		if (electricityMetersPage) {
			pageRouter.navigateToRoute(electricityMetersPage.data.uid)
		} else {
			log.warnFrom('goToMetersPage', 'Could not find electricity meters page. Semantics required:', [ SEMANTIC_ELECTRICITY, SEMANTIC_METERS ])
		}
	}
	
	const verifyCurrentSession = async () => {
		const currentURL = new URL(window.location.href)
		const returnStatus = currentURL.searchParams.has('status') ? currentURL.searchParams.get('status') : null
		const sessionID = currentURL.searchParams.has('signicatSessionId') ? currentURL.searchParams.get('signicatSessionId') : null
		
		if (!returnStatus) {
			log.debugFrom('verifyCurrentSession', 'No status param, neutral state')
			return
		}
		
		if (returnStatus === 'completed' && sessionID) {
			setProcessing(true)
			
			try {
				const data = await useBackend().getConsentData(useAPIStore().token!, sessionID)
				
				log.infoFrom('verifyCurrentSession', 'Consent verified', data.message)
				
				model.value = {
					status: 'success',
					message: data.message,
					data
				}
				
				useLogsStore().event('Elhub consent verified')
				emit('update:modelValue', model.value)
				
				await sleep(200)
				const pageStore = usePageStore()
				
				pageStore.validate().then(() => {
					log.infoFrom('verifyCurrentSession', 'Page re-validated', props.hostPage?.data.validated)
					EventBus.$emit('navigation:backup')
				}).catch(e => {
					log.always.errorFrom('verifyCurrentSession', 'Page re-validation failed', e)
				})
			} catch (e: any) {
				log.always.errorFrom('verifyCurrentSession', 'Consent verification failed', e)
				
				if (isElhubConsentErrorResponse(e.response.data)) {
					model.value = {
						status: 'error',
						message: e.response.data.error,
						error: e.response.data
					}
				} else {
					model.value = {
						status: 'error',
						message: props.genericErrorLabel ?? e.message,
						error: e
					}
				}
				
				useLogsStore().error('[ElhubController]', 'Consent verification failed', model.value!.message)
				emit('update:modelValue', model.value)
			} finally {
				setProcessing(false)
				
				setTimeout(() => {
					EventBus.$emit('navigation:scroll-to-error')
				}, 100)
			}
		} else if (returnStatus == 'rejected') {
			model.value = {
				status: 'error',
				message: props.rejectedLabel ?? 'Rejected'
			}
			
			useLogsStore().warning('[ElhubController]', 'Rejected')
			emit('update:modelValue', model.value)
		} else if (returnStatus == 'postponed') {
			model.value = {
				status: 'error',
				message: props.postponedLabel ?? 'Postponed'
			}
			
			useLogsStore().warning('[ElhubController]', 'Postponed')
			emit('update:modelValue', model.value)
		} else {
			log.warnFrom('verifyCurrentSession', 'Non-success status from Elhub', returnStatus, sessionID)
		}
		
		const query = {
			...$route.query,
			status: undefined,
			signicatSessionId: undefined
		}
		
		await $router.replace({
			...$route,
			query: query
		})
	}
	
	const openPersonNumberModal = async () => {
		if ($personNumberModal.value) {
			await $personNumberModal.value.openModal()
		}
	}
	
	const closePersonNumberModal = async () => {
		if ($personNumberModal.value) {
			await $personNumberModal.value.closeModal()
		}
	}
	
	watch((() => props.hostPage?.data.validated), (nowValidated, previouslyValidated) => {
		if (!nowValidated && model.value?.status === 'success') {
			// Page became invalid, reset
			log.infoFrom('watch:hostPage', 'Host page not validated, resetting…')
			reset()
		}
	}, {
		immediate: true
	})
	
	watch(errorCode, async (newCode) => {
		if (newCode === ElhubConsentErrorCode.ELHUB_INVALID_SSN) {
			await nextTick()
			await openPersonNumberModal()
		}
	})
	
	onMounted(() => {
		verifyCurrentSession()
	})
	
	defineExpose({
		...base.expose
	})
</script>

<style lang="scss">
    @use '@/assets/mixins.scss' as m;
    
    .acquisit-elhub-response {
	    --border-color: #{m.color("border")};
	    
	    .processing {
		    background: v-bind("color.backgroundLightTinted.value.cssHSLA");
		    border-radius: 6px;
		    padding: 20px;
	    }
		
	    .error-code {
		    font-family: var(--font-family-monospace, m.font("monospace"));
		    font-size: 0.8rem;
		    font-weight: 900;
		    font-style: italic;
	    }
	    
	    .actions {
		    margin-top: 16px;
		    display: flex;
		    gap: 16px;
	    }
	    
	    &:has(+ .acquisit-component) {
		    
		    &.status-error,
		    &.status-success,
		    &.status-processing {
			    border-bottom: 1px solid var(--border-color);
			    padding-bottom: 24px;
		    }
		    
		    &.status-error,
		    &.status-processing {
			    margin-bottom: 24px;
		    }
		    
		    &.status-success {
			    padding-bottom: 48px;
			    margin-bottom: 48px;
		    }
	    }
    }
    
    .acquisit-component + .acquisit-elhub-response {
	    
	    &.status-error,
	    &.status-success,
	    &.status-processing {
		    border-top: 1px solid var(--border-color);
		    padding-top: 24px;
	    }
	    
	    &.status-error,
	    &.status-processing {
		    margin-top: 24px;
	    }
	    
	    &.status-success {
		    padding-top: 48px;
		    margin-top: 48px;
	    }
    }
</style>
